import { useState, useMemo, useEffect } from "react";
import React from "react";
import Select, { components } from "react-select";
import axios from "axios";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import { numeroPerExcel, scaricaExcel, FMT_EURO, FMT_PERC } from "../exportExcel";
import "./BilancinoAnalitico.css";
import "../Ricavi/Ricavi.css";
import "../../OperazioniAutomezzi/ControlloKmOre/ControlloKmOre.css";
import "../../HrGenerale/TrendKpi/TrendKpi.css";
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// ── Opzioni dropdown ──
const colonneOptions = [
    { value: "anno", label: "Anno" },
    { value: "trimestre", label: "Trimestre" },
    { value: "mese", label: "Mese" },
];

const righeOptions = [
    { value: "tipo", label: "Tipo" },
    { value: "macro_voce_analitica", label: "MacroVoce Analitica" },
    { value: "albero2", label: "Albero 2" },
    { value: "albero3", label: "Albero 3" },
    { value: "albero4", label: "Albero 4" },
    { value: "codice_conto", label: "Codice Conto" },
    { value: "des_conto", label: "Conto" },
];

const indicatoreOptions = [
    { value: "saldo_voci", label: "Saldo_Voci" },
    { value: "perc_costi", label: "Perc. Costi" },
];

// ── Formatters ──
const fmtEuroK = (v) => {
    if (v == null) return "";
    if (v === 0) return "0K €";
    if (Math.abs(v) >= 1_000_000) {
        return `${(v / 1_000_000).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}Mln €`;
    }
    const k = v / 1000;
    return `${Math.round(k).toLocaleString("it-IT")}K €`;
};

const fmtPerc = (v) => {
    if (v == null) return "";
    if (v === Infinity || v === -Infinity) return "Infinito";
    return `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const nomiMesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const meseToTrimestre = { 1: 1, 2: 1, 3: 1, 4: 2, 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4, 11: 4, 12: 4 };

// ── React-select: mostra max 2 valori, poi badge +N ──
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);
    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return <div className="controllo-km-ore-select-more-badge">+{values.length - 2}</div>;
    }
    return null;
};

const FullscreenIcon = ({ active }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {active ? (
            <>
                <polyline points="4 14 10 14 10 20"></polyline>
                <polyline points="20 10 14 10 14 4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        ) : (
            <>
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        )}
    </svg>
);

// ── Aggregazione ricavi per periodo (denominatore di Perc. Costi) ──
// Da [{anno, mese, ricavi}] costruisce un dict periodo → ricavi totali su quell'anno/trim/mese.
const buildRicaviPerPeriodo = (ricaviPerMese) => {
    const bucket = {};
    const accumula = (k, v) => { bucket[k] = (bucket[k] || 0) + v; };
    ricaviPerMese.forEach(r => {
        const v = Number(r.ricavi) || 0;
        const mKey = `${r.anno}-${String(r.mese).padStart(2, "0")}`;
        const tKey = `${r.anno}-trim${meseToTrimestre[r.mese]}`;
        const yKey = `${r.anno}`;
        [mKey, tKey, yKey, "totale"].forEach(k => accumula(k, v));
    });
    return bucket;
};

// Aggrega le righe Y in dict periodo → {saldo_voci, perc_costi}.
// - saldo_voci: somma di importo_leggibile (cambiato di segno) di tutte le righe del nodo
// - perc_costi: |costi_voci della riga| / ricavi del periodo × 100 (formula B: peso costi sui ricavi)
//   "costi_voci" è il sotto-saldo che considera SOLO le righe con account_type expense*
//   (filtrato già lato backend). Questo allinea la cella "Totale/Totale" della tabella
//   alla card "Perc. Costi" (entrambe = |Costi|/Ricavi).
const buildPeriodi = (rows, ricaviPerPeriodo) => {
    const buckSaldo = {};
    const buckCosti = {};
    const accumula = (target, key, valore) => { target[key] = (target[key] || 0) + valore; };

    rows.forEach(r => {
        const saldo = Number(r.saldo_voci) || 0;
        const costi = Number(r.costi_voci) || 0;
        const mKey = `${r.anno}-${String(r.mese).padStart(2, "0")}`;
        const tKey = `${r.anno}-trim${meseToTrimestre[r.mese]}`;
        const yKey = `${r.anno}`;
        [mKey, tKey, yKey, "totale"].forEach(k => {
            accumula(buckSaldo, k, saldo);
            accumula(buckCosti, k, costi);
        });
    });

    const periodi = {};
    const allKeys = new Set([...Object.keys(buckSaldo), ...Object.keys(buckCosti)]);
    allKeys.forEach(k => {
        const saldo = buckSaldo[k];
        const costi = buckCosti[k];
        const ricavi = ricaviPerPeriodo[k];
        let perc;
        if (costi == null) {
            perc = null;
        } else if (ricavi == null || ricavi === 0) {
            perc = costi === 0 ? null : Infinity;
        } else {
            perc = Math.round((Math.abs(costi) / ricavi) * 1000) / 10;
        }
        periodi[k] = {
            saldo_voci: saldo,
            perc_costi: perc,
        };
    });
    return periodi;
};

// Costruisce albero gerarchico per riga in base a campiGerarchia.
// Se la dimensione "tipo" (ALBERO1) è inclusa nella gerarchia, marca ogni nodo col
// suo "tipoRiga" (valore della dimensione tipo lungo il path). I nodi che non sono
// sotto la categoria "C" hanno perc_costi forzato a null in tutti i periodi:
// la metrica "Perc. Costi" è significativa solo per le righe di costi.
const buildAlbero = (rows, campiGerarchia, ricaviPerPeriodo) => {
    const radice = { byKey: {} };
    const tipoIdx = campiGerarchia.indexOf("tipo");

    // Un livello per ogni campo selezionato, senza limite fisso: con 7 dimensioni
    // disponibili l'utente può sceglierle tutte.
    const aggiungi = (r) => {
        let nodo = radice;
        for (const c of campiGerarchia) {
            const v = r[c] ?? "Non assegnato";
            if (!nodo.byKey[v]) {
                nodo.byKey[v] = { label: v, rows: [], byKey: {} };
            }
            nodo = nodo.byKey[v];
            nodo.rows.push(r);
        }
    };
    rows.forEach(r => aggiungi(r));

    const materializza = (nodo, level, pathKey, pathVals) => {
        return Object.keys(nodo.byKey).sort().map(k => {
            const n = nodo.byKey[k];
            const id = `${pathKey}/${k}`;
            const newPathVals = [...pathVals, k];
            let periodi = buildPeriodi(n.rows, ricaviPerPeriodo);

            // Se conosciamo il tipo della riga e non è "C", nascondi perc_costi.
            const tipoRiga = tipoIdx >= 0 && tipoIdx < newPathVals.length ? newPathVals[tipoIdx] : undefined;
            if (tipoRiga !== undefined && tipoRiga !== "C") {
                const nascosti = {};
                Object.keys(periodi).forEach(pk => {
                    nascosti[pk] = { ...periodi[pk], perc_costi: null };
                });
                periodi = nascosti;
            }

            return {
                id, label: n.label, level,
                periodi,
                children: materializza(n, level + 1, id, newPathVals),
            };
        });
    };

    return materializza(radice, 0, "", []);
};

const estraiPeriodiDisponibili = (rows) => {
    const anniSet = new Set();
    const mesiPerAnno = {};
    rows.forEach(r => {
        anniSet.add(r.anno);
        if (!mesiPerAnno[r.anno]) mesiPerAnno[r.anno] = new Set();
        mesiPerAnno[r.anno].add(r.mese);
    });
    return {
        anni: [...anniSet].sort(),
        mesiPerAnno: Object.fromEntries(
            Object.entries(mesiPerAnno).map(([a, s]) => [a, [...s].sort((x, y) => x - y)])
        ),
    };
};

// ── Sub-componente: 5 card riassuntive ──
// Pattern stilistico allineato a TrendPeriodo (.oper-hr-kpi-card): icona con gradient + content.
const ICON_SALDO = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v18"></path><path d="M3 8h18"></path><path d="M6 8l-3 6a3 3 0 0 0 6 0z"></path><path d="M18 8l-3 6a3 3 0 0 0 6 0z"></path>
    </svg>
);
const ICON_RICAVI = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline>
    </svg>
);
const ICON_COSTI = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline><polyline points="17 18 23 18 23 12"></polyline>
    </svg>
);
const ICON_PERC = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="5" x2="5" y2="19"></line><circle cx="6.5" cy="6.5" r="2.5"></circle><circle cx="17.5" cy="17.5" r="2.5"></circle>
    </svg>
);
const ICON_AMMORT = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="20" x2="12" y2="10"></line><line x1="18" y1="20" x2="18" y2="4"></line><line x1="6" y1="20" x2="6" y2="16"></line>
    </svg>
);

const CardsRiassuntive = ({ saldo = null, ricavi = null, costi = null, percCosti = null, totAmmort = null }) => {
    const cards = [
        { label: "Saldo",       valore: saldo,     formatter: fmtEuroK, iconCls: "saldo",   icon: ICON_SALDO },
        { label: "Ricavi",      valore: ricavi,    formatter: fmtEuroK, iconCls: "ricavi",  icon: ICON_RICAVI },
        { label: "Costi",       valore: costi,     formatter: fmtEuroK, iconCls: "costi",   icon: ICON_COSTI },
        { label: "Perc. Costi", valore: percCosti, formatter: fmtPerc,  iconCls: "perc",    icon: ICON_PERC },
        { label: "Tot Ammort.", valore: totAmmort, formatter: fmtEuroK, iconCls: "ammort",  icon: ICON_AMMORT },
    ];
    return (
        <div className="admin-bilancino-cards">
            {cards.map(c => {
                const valCls = c.valore == null || c.valore === Infinity ? "" : c.valore < 0 ? "negative" : c.valore > 0 ? "positive" : "";
                return (
                    <div key={c.label} className="admin-bilancino-card">
                        <div className={`admin-bilancino-card-icon ${c.iconCls}`}>{c.icon}</div>
                        <div className="admin-bilancino-card-content">
                            <div className="admin-bilancino-card-label">{c.label}</div>
                            <div className={`admin-bilancino-card-value ${valCls}`}>
                                {c.valore != null ? c.formatter(c.valore) : "—"}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const BilancinoAnalitico = ({ filters }) => {
    // Stato dati
    const [rows, setRows] = useState([]);
    const [ricaviPerMese, setRicaviPerMese] = useState([]);
    const [summary, setSummary] = useState({ ricavi: null, costi: null, ammort: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fullscreen tabella
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);

    // Tabella parametrica — default: tutte le colonne (Anno+Trim+Mese) / gerarchia Tipo > MacroVoce > Albero3 > Conto / entrambi gli indicatori
    const [selectedColonne, setSelectedColonne] = useState([...colonneOptions]);
    const [selectedRighe, setSelectedRighe] = useState(() =>
        ["tipo", "macro_voce_analitica", "albero3", "des_conto"].map(v => righeOptions.find(o => o.value === v))
    );
    const [selectedIndicatore, setSelectedIndicatore] = useState([...indicatoreOptions]);
    const [expandedRows, setExpandedRows] = useState(new Set());

    // ── Fetch dataset ──
    useEffect(() => {
        if (!filters?.fromDate || !filters?.toDate) return;
        const controller = new AbortController();
        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("token");
                const settoreCsv = (filters.settore || []).map(s => s.value).join(",");
                const commessaCsv = (filters.commessa || []).map(s => s.value).join(",");
                const clientiCsv = (filters.clienti || []).map(s => s.value).join(",");
                const fornitoriCsv = (filters.fornitori || []).map(s => s.value).join(",");
                const partnerCsv = [clientiCsv, fornitoriCsv].filter(Boolean).join(",");
                const voceAnaliticaCsv = (filters.voceAnalitica || []).map(s => s.value).join(",");
                const sedeCsv = (filters.sede || []).map(s => s.value).join(",");
                const params = { from: filters.fromDate, to: filters.toDate };
                if (settoreCsv) params.settore = settoreCsv;
                if (commessaCsv) params.commessa = commessaCsv;
                if (partnerCsv) params.cliente_fornitore = partnerCsv;
                if (voceAnaliticaCsv) params.voce_analitica = voceAnaliticaCsv;
                if (sedeCsv) params.sede = sedeCsv;

                const { data } = await axios.get(`${API_URL}/admin/bilancino/dataset`, {
                    params,
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (cancelled) return;
                setRows(Array.isArray(data?.rows) ? data.rows : []);
                setRicaviPerMese(Array.isArray(data?.ricaviPerMese) ? data.ricaviPerMese : []);
                setSummary({
                    ricavi: data?.summary?.ricavi ?? null,
                    costi: data?.summary?.costi ?? null,
                    ammort: data?.summary?.ammort ?? null,
                });
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                if (cancelled) return;
                console.error("Errore caricamento dataset Bilancino Analitico:", err);
                setError(err.response?.data?.error || err.message || "Errore caricamento dati");
                setRows([]);
                setRicaviPerMese([]);
                setSummary({ ricavi: null, costi: null, ammort: null });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => { cancelled = true; controller.abort(); };
    }, [filters]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") setIsFullscreenTable(false);
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, []);

    // Espansione di default: apri tutti i nodi dei livelli precedenti a "MacroVoce Analitica"
    // così che la tabella mostri al primo render le righe fino al livello macro voce.
    // L'effetto scatta a ogni ricostruzione dell'albero (cambio filtri o cambio dimensioni di riga).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedRigheKey = selectedRighe.map(r => r.value).join("|");
    useEffect(() => {
        const macroIdx = selectedRighe.findIndex(r => r.value === "macro_voce_analitica");
        if (macroIdx <= 0) {
            setExpandedRows(new Set());
            return;
        }
        const toExpand = new Set();
        const walk = (nodi, level) => {
            nodi.forEach(n => {
                if (level < macroIdx && n.children && n.children.length > 0) {
                    toExpand.add(n.id);
                    walk(n.children, level + 1);
                }
            });
        };
        // eslint-disable-next-line no-use-before-define
        walk(albero, 0);
        setExpandedRows(toExpand);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, selectedRigheKey]);

    // ── Valori card riassuntive ──
    // - "costi" dal backend include già gli ammortamenti (allineato a riga "C" della tabella).
    // - Saldo = Ricavi + Costi (Ammort già dentro Costi, non sommarlo di nuovo).
    // - Perc.Costi = |Costi| / Ricavi × 100 — formula "peso dei costi sui ricavi".
    //   La colonna "Perc. Costi" della tabella usa la stessa formula applicata
    //   ai costi_voci della singola riga: alla riga "Totale" colonna "Totale" il valore
    //   coincide con questa card.
    // - Tot Ammort. è esposto come sotto-totale dei Costi (a fini di drill-down visivo).
    const cardsValues = useMemo(() => {
        const { ricavi, costi, ammort } = summary;
        const saldo = (ricavi == null || costi == null) ? null : ricavi + costi;
        let percCosti = null;
        if (costi != null && ricavi != null) {
            if (ricavi === 0) percCosti = costi === 0 ? null : Infinity;
            else percCosti = Math.round((Math.abs(costi) / ricavi) * 1000) / 10;
        }
        return { saldo, ricavi, costi, percCosti, totAmmort: ammort };
    }, [summary]);

    // ── Aggregazioni ──
    const ricaviPerPeriodo = useMemo(() => buildRicaviPerPeriodo(ricaviPerMese), [ricaviPerMese]);

    const campiGerarchia = selectedRighe.length > 0 ? selectedRighe.map(r => r.value) : ["tipo"];
    const albero = useMemo(
        () => buildAlbero(rows, campiGerarchia, ricaviPerPeriodo),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, ricaviPerPeriodo, campiGerarchia.join("|")]
    );

    const { anni, mesiPerAnno } = useMemo(() => estraiPeriodiDisponibili(rows), [rows]);
    const totaliGlobali = useMemo(() => buildPeriodi(rows, ricaviPerPeriodo), [rows, ricaviPerPeriodo]);

    const haAnnoSelezionato = selectedColonne.some(o => o.value === "anno");
    const haTrimestreSelezionato = selectedColonne.some(o => o.value === "trimestre");
    const haMeseSelezionato = selectedColonne.some(o => o.value === "mese");

    const isMeseValido = (anno, mese) => (mesiPerAnno[anno] || []).includes(mese);
    const isTrimestreValido = (anno, trim) => (mesiPerAnno[anno] || []).some(m => meseToTrimestre[m] === trim);

    const periodiDaMostrare = useMemo(() => {
        if (selectedColonne.length === 0) {
            return [...anni.map(a => ({ chiave: `${a}`, etichetta: `${a}`, anno: a })), { chiave: "totale", etichetta: "Totale", anno: "totale" }];
        }
        const periodi = [];
        anni.forEach(anno => {
            if (haMeseSelezionato) {
                for (let m = 1; m <= 12; m++) {
                    if (isMeseValido(anno, m)) {
                        periodi.push({ chiave: `${anno}-${String(m).padStart(2, "0")}`, etichetta: nomiMesi[m - 1], anno });
                    }
                }
            } else if (haTrimestreSelezionato) {
                for (let t = 1; t <= 4; t++) {
                    if (isTrimestreValido(anno, t)) {
                        periodi.push({ chiave: `${anno}-trim${t}`, etichetta: `T${t}`, anno });
                    }
                }
            } else if (haAnnoSelezionato) {
                periodi.push({ chiave: `${anno}`, etichetta: `${anno}`, anno });
            }
        });
        periodi.push({ chiave: "totale", etichetta: "Totale", anno: "totale" });
        return periodi;
    }, [selectedColonne, anni, mesiPerAnno]); // eslint-disable-line react-hooks/exhaustive-deps

    const isColonnaVisibile = (chiaveInd) => selectedIndicatore.length === 0 ? true : selectedIndicatore.some(o => o.value === chiaveInd);

    const colonnePerPeriodo = useMemo(() => {
        let c = 0;
        if (isColonnaVisibile("saldo_voci")) c++;
        if (isColonnaVisibile("perc_costi")) c++;
        return c || 1;
    }, [selectedIndicatore]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleRow = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // ── Render metriche per un periodo ──
    const renderMetriche = (periodo, key) => {
        const cells = [];
        if (isColonnaVisibile("saldo_voci")) {
            const v = periodo?.saldo_voci;
            const cls = v == null ? "" : v < 0 ? "value-negative" : v > 0 ? "value-positive" : "";
            cells.push(<td key={`${key}-saldo`} className={cls}>{v != null ? fmtEuroK(v) : ""}</td>);
        }
        if (isColonnaVisibile("perc_costi")) {
            const v = periodo?.perc_costi;
            cells.push(<td key={`${key}-perc`}>{v != null ? fmtPerc(v) : "—"}</td>);
        }
        return cells;
    };

    // ── Render righe (ricorsivo) ──
    const renderNodi = (nodi) => {
        const output = [];
        nodi.forEach(nodo => {
            const haFigli = nodo.children && nodo.children.length > 0;
            const isExpanded = expandedRows.has(nodo.id);
            const icona = haFigli ? (isExpanded ? "▼" : "▶") : "•";
            const classLevel = `tr-level-${nodo.level + 1}`;
            const classCell = `td-settore td-indent-${nodo.level}${haFigli ? " td-expandable" : ""}`;

            output.push(
                <tr key={nodo.id} className={classLevel}>
                    <td className={classCell} onClick={haFigli ? () => toggleRow(nodo.id) : undefined}>
                        <span className="expand-icon">{icona}</span>
                        <span className="level-value">{nodo.label}</span>
                    </td>
                    {periodiDaMostrare.map(p => (
                        <React.Fragment key={p.chiave}>
                            {renderMetriche(nodo.periodi[p.chiave], p.chiave)}
                        </React.Fragment>
                    ))}
                </tr>
            );
            if (isExpanded && haFigli) {
                output.push(...renderNodi(nodo.children));
            }
        });
        return output;
    };

    // ── Export Excel ──
    // Il .xlsx lo genera il backend (controller condiviso `downloadFile` in
    // operazioniHrController.js): qui prepariamo solo il payload nel formato
    // "header raggruppati", lo stesso di StatusNoleggi.prepareExportDataGeneric.
    const prepareExportData = () => {
        // Riga 1: un gruppo per periodo, largo quanti indicatori sono visibili.
        // L'anno va esplicitato: su un range multi-anno "Ottobre" da solo è ambiguo.
        const headerGroups = periodiDaMostrare.map(p => ({
            label: p.anno && p.anno !== "totale" && p.etichetta !== String(p.anno)
                ? `${p.etichetta} ${p.anno}`
                : p.etichetta,
            span: colonnePerPeriodo,
        }));

        // Riga 2: sotto-header ripetuti per ogni periodo. La cella [0] porta la gerarchia
        // selezionata — in A1 il controller condiviso scrive "Raggruppamento", hardcoded.
        const headers = [selectedRighe.map(r => r.label).join(" > ") || "Riga"];
        periodiDaMostrare.forEach(() => {
            if (isColonnaVisibile("saldo_voci")) headers.push("Saldo_Voci");
            if (isColonnaVisibile("perc_costi")) headers.push("Perc. Costi");
        });

        // Righe dati: l'albero completo, non solo i nodi espansi a video.
        const rows = [];
        const appendNodo = (nodo) => {
            const cells = ["  ".repeat(nodo.level) + nodo.label];
            periodiDaMostrare.forEach(p => {
                const periodo = nodo.periodi[p.chiave] || {};
                if (isColonnaVisibile("saldo_voci")) cells.push(numeroPerExcel(periodo.saldo_voci));
                if (isColonnaVisibile("perc_costi")) cells.push(numeroPerExcel(periodo.perc_costi));
            });
            rows.push(cells);
            if (nodo.children) nodo.children.forEach(appendNodo);
        };
        albero.forEach(appendNodo);

        // Riga finale: il backend mette in grassetto l'ultima riga del foglio.
        const totRow = ["Totale"];
        periodiDaMostrare.forEach(p => {
            const t = totaliGlobali[p.chiave] || {};
            if (isColonnaVisibile("saldo_voci")) totRow.push(numeroPerExcel(t.saldo_voci));
            if (isColonnaVisibile("perc_costi")) totRow.push(numeroPerExcel(t.perc_costi));
        });
        rows.push(totRow);

        // Formato cella per colonna, ricavato dai sotto-header appena costruiti.
        const numFmt = headers.map(h => h === "Saldo_Voci" ? FMT_EURO : h === "Perc. Costi" ? FMT_PERC : "");

        return { headerGroups, headers, rows, formattazione: { numFmt, negativiRossi: true, freeze: true } };
    };

    const handleExport = async () => {
        try {
            await scaricaExcel({
                endpoint: "admin/bilancino/downloadExcel",
                tableName: "Bilancino Analitico",
                datiDaStampare: prepareExportData(),
                filename: "bilancino_analitico.xlsx",
            });
        } catch (err) {
            console.error("Errore durante il download del file:", err);
            setError("Errore durante l'esportazione del file Excel");
        }
    };

    const labelRighe = selectedRighe.length > 0 ? selectedRighe[0].label : "Tipo";

    if (loading) {
        return (
            <div className="admin-bilancino-container">
                <Lf23Loader />
            </div>
        );
    }

    return (
        <div className="admin-bilancino-container">
            {/* ═══ Row 1: Cards riassuntive ═══ */}
            <CardsRiassuntive
                saldo={cardsValues.saldo}
                ricavi={cardsValues.ricavi}
                costi={cardsValues.costi}
                percCosti={cardsValues.percCosti}
                totAmmort={cardsValues.totAmmort}
            />

            {error && (
                <div className="admin-bilancino-error">
                    {error}
                </div>
            )}

            {/* ═══ Row 2: Tabella parametrica ═══ */}
            <div className={`controllo-km-ore-table-section${isFullscreenTable ? " fullscreen-table" : ""}`}>
                <RotateDevice active={isFullscreenTable} />

                <div className="controllo-km-ore-table-header">
                    <span className="controllo-km-ore-table-title">Bilancino Analitico</span>
                    <div className="controllo-km-ore-table-header-btn">
                        <button className="controllo-km-ore-download-btn" onClick={handleExport} title="Esporta in Excel" disabled={albero.length === 0}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Esporta
                        </button>
                        <button className="controllo-km-ore-fullscreen-btn" onClick={() => setIsFullscreenTable(!isFullscreenTable)} title={isFullscreenTable ? "Esci da tutto schermo" : "Tutto schermo"}>
                            <FullscreenIcon active={isFullscreenTable} />
                        </button>
                    </div>
                </div>

                <div className="controllo-km-ore-config-row">
                    <div className="controllo-km-ore-config-item">
                        <label>COLONNE</label>
                        <Select
                            classNamePrefix="controllo-km-ore-select"
                            placeholder="Selezioni multiple"
                            value={selectedColonne}
                            options={colonneOptions}
                            onChange={setSelectedColonne}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ MultiValue: MultiValueContainer }}
                        />
                    </div>
                    <div className="controllo-km-ore-config-item">
                        <label>RIGHE</label>
                        <Select
                            classNamePrefix="controllo-km-ore-select"
                            placeholder="Selezioni multiple"
                            value={selectedRighe}
                            options={righeOptions}
                            onChange={setSelectedRighe}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ MultiValue: MultiValueContainer }}
                        />
                    </div>
                    <div className="controllo-km-ore-config-item">
                        <label>INDICATORE</label>
                        <Select
                            classNamePrefix="controllo-km-ore-select"
                            placeholder="Tutti"
                            value={selectedIndicatore}
                            options={indicatoreOptions}
                            onChange={setSelectedIndicatore}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ MultiValue: MultiValueContainer }}
                        />
                    </div>
                </div>

                <div className="controllo-km-ore-table-wrapper">
                    <table className="controllo-km-ore-table">
                        <thead>
                            {haAnnoSelezionato && (
                                <tr>
                                    <th className="th-settore">Anno</th>
                                    {(() => {
                                        const celle = [];
                                        anni.forEach(anno => {
                                            let colSpan = colonnePerPeriodo;
                                            if (haMeseSelezionato) {
                                                let mesiValidi = 0;
                                                for (let m = 1; m <= 12; m++) if (isMeseValido(anno, m)) mesiValidi++;
                                                colSpan = mesiValidi * colonnePerPeriodo;
                                            } else if (haTrimestreSelezionato) {
                                                let trimValidi = 0;
                                                for (let t = 1; t <= 4; t++) if (isTrimestreValido(anno, t)) trimValidi++;
                                                colSpan = trimValidi * colonnePerPeriodo;
                                            }
                                            if (colSpan > 0) celle.push(<th key={anno} colSpan={colSpan} className="th-periodo">{anno}</th>);
                                        });
                                        let rowSpanTot = 1;
                                        if (haTrimestreSelezionato) rowSpanTot++;
                                        if (haMeseSelezionato) rowSpanTot++;
                                        celle.push(<th key="tot" colSpan={colonnePerPeriodo} rowSpan={rowSpanTot} className="th-periodo th-totale-header">Totale</th>);
                                        return celle;
                                    })()}
                                </tr>
                            )}
                            {haTrimestreSelezionato && (
                                <tr>
                                    <th className="th-settore">Trimestre</th>
                                    {(() => {
                                        const celle = [];
                                        const mesiPerTrim = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };
                                        const colSpanTrim = (anno, trim) => {
                                            if (!haMeseSelezionato) return colonnePerPeriodo;
                                            return mesiPerTrim[trim].filter(m => isMeseValido(anno, m)).length * colonnePerPeriodo;
                                        };
                                        anni.forEach(anno => {
                                            for (let t = 1; t <= 4; t++) {
                                                if (isTrimestreValido(anno, t)) {
                                                    celle.push(<th key={`${anno}-t${t}`} colSpan={colSpanTrim(anno, t)} className="th-periodo">Q{t} {anno}</th>);
                                                }
                                            }
                                        });
                                        if (!haAnnoSelezionato) {
                                            let rowSpanTot = 1;
                                            if (haMeseSelezionato) rowSpanTot++;
                                            celle.push(<th key="tot" colSpan={colonnePerPeriodo} rowSpan={rowSpanTot} className="th-periodo th-totale-header">Totale</th>);
                                        }
                                        return celle;
                                    })()}
                                </tr>
                            )}
                            {haMeseSelezionato && (
                                <tr>
                                    <th className="th-settore">Mese</th>
                                    {(() => {
                                        const celle = [];
                                        anni.forEach(anno => {
                                            for (let m = 1; m <= 12; m++) {
                                                if (isMeseValido(anno, m)) {
                                                    celle.push(<th key={`${anno}-m${m}`} colSpan={colonnePerPeriodo} className="th-periodo">{nomiMesi[m - 1]} {anno}</th>);
                                                }
                                            }
                                        });
                                        if (!haAnnoSelezionato && !haTrimestreSelezionato) {
                                            celle.push(<th key="tot" colSpan={colonnePerPeriodo} className="th-periodo th-totale-header">Totale</th>);
                                        }
                                        return celle;
                                    })()}
                                </tr>
                            )}
                            <tr>
                                <th className="th-settore">{labelRighe}</th>
                                {periodiDaMostrare.map(p => {
                                    const cls = p.chiave === "totale" ? "th-metrica th-metrica-totale" : "th-metrica";
                                    return (
                                        <React.Fragment key={p.chiave}>
                                            {isColonnaVisibile("saldo_voci") && <th className={cls}>Saldo_Voci</th>}
                                            {isColonnaVisibile("perc_costi") && <th className={cls}>Perc. Costi</th>}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {albero.length > 0 ? renderNodi(albero) : (
                                <tr>
                                    <td className="td-settore" colSpan={100} style={{ textAlign: "center", color: "#94a3b8", fontStyle: "italic", padding: "40px" }}>
                                        {rows.length === 0 ? "Nessun dato disponibile" : "Seleziona almeno una dimensione riga per visualizzare i dati"}
                                    </td>
                                </tr>
                            )}
                            <tr className="tr-totali">
                                <td className="td-settore"><strong>Totale</strong></td>
                                {periodiDaMostrare.map(p => {
                                    const t = totaliGlobali[p.chiave] || {};
                                    return (
                                        <React.Fragment key={p.chiave}>
                                            {isColonnaVisibile("saldo_voci") && (
                                                <td className={t.saldo_voci != null && t.saldo_voci < 0 ? "value-negative" : t.saldo_voci > 0 ? "value-positive" : ""}>
                                                    <strong>{t.saldo_voci != null ? fmtEuroK(t.saldo_voci) : ""}</strong>
                                                </td>
                                            )}
                                            {isColonnaVisibile("perc_costi") && (
                                                <td>
                                                    <strong>{t.perc_costi != null ? fmtPerc(t.perc_costi) : ""}</strong>
                                                </td>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default BilancinoAnalitico;
